import { useEffect, useState, useCallback } from "react";
import { Download, X, Share, PlusSquare, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { installStore } from "@/lib/installStore";
import { tokenStore } from "@/lib/api";

// Bumped so the old dismissal doesn't suppress the redesigned popup.
const DISMISS_KEY = "pmx_install_dismissed_v2";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIosDevice = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent || "") && !window.MSStream;

// Prominent, centered "Install Cross Jobs" popup. Auto-shows once on first load
// when installable, and can be re-opened any time from the header Install button
// (via the `pmx-open-install` event) even after being dismissed.
export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(installStore.get());
  const [open, setOpen] = useState(false);
  const ios = isIosDevice();

  useEffect(() => installStore.subscribe(() => setDeferred(installStore.get())), []);

  // Auto-open once on first eligible load.
  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    const path = window.location.pathname;
    if (path === "/home" || path.startsWith("/pay/") || path.startsWith("/join/") || (path === "/" && !tokenStore.get())) return;
    if (deferred || ios) {
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [deferred, ios]);

  // Manual re-open from the header button — ignores the dismiss flag.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("pmx-open-install", onOpen);
    return () => window.removeEventListener("pmx-open-install", onOpen);
  }, []);

  const dismiss = useCallback(() => { setOpen(false); localStorage.setItem(DISMISS_KEY, "1"); }, []);

  const install = async () => {
    const evt = installStore.get();
    if (!evt) return;
    evt.prompt();
    try { await evt.userChoice; } catch { /* user dismissed */ }
    installStore.clear();
    setOpen(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm fade-up" data-testid="pwa-install-prompt">
      <div className="relative w-full max-w-sm rounded-3xl border border-border bg-card shadow-2xl p-6 text-center">
        <button data-testid="pwa-install-dismiss" onClick={dismiss} aria-label="Dismiss"
          className="absolute top-3 right-3 rounded-full w-8 h-8 inline-flex items-center justify-center text-muted-foreground hover:bg-secondary">
          <X className="w-4 h-4" />
        </button>

        <img src="/icon-cj-192.png" alt="Cross Jobs" className="w-20 h-20 rounded-2xl mx-auto shadow-md" />
        <h2 className="font-display text-2xl font-bold mt-4">Install Cross Jobs</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Add Cross Jobs to your home screen for quick, full-screen, app-like access to your jobs and timesheets.
        </p>

        {ios ? (
          <div className="mt-6 rounded-2xl bg-secondary/60 p-4 text-left text-sm space-y-2" data-testid="pwa-ios-steps">
            <p className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-bold shrink-0">1</span>
              Tap the <Share className="inline w-4 h-4 mx-0.5 -mt-0.5" /> <span className="font-medium">Share</span> button in Safari.
            </p>
            <p className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-bold shrink-0">2</span>
              Choose <PlusSquare className="inline w-4 h-4 mx-0.5 -mt-0.5" /> <span className="font-medium">Add to Home Screen</span>.
            </p>
          </div>
        ) : deferred ? (
          <Button data-testid="pwa-install-btn" onClick={install}
            className="mt-6 w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-white text-base font-semibold">
            <Download className="w-5 h-5 mr-2" /> Install app
          </Button>
        ) : (
          <div className="mt-6 rounded-2xl bg-secondary/60 p-4 text-left text-sm" data-testid="pwa-manual-steps">
            <p className="flex items-center gap-2">
              Open your browser menu <MoreVertical className="inline w-4 h-4 -mt-0.5" /> and choose
              <span className="font-medium">&nbsp;“Install app”</span> or <span className="font-medium">“Add to Home screen”</span>.
            </p>
          </div>
        )}

        <button onClick={dismiss} data-testid="pwa-install-later"
          className="mt-4 text-sm text-muted-foreground hover:text-foreground font-medium">
          Maybe later
        </button>
      </div>
    </div>
  );
};
